import { measureLatency } from '../gctl-measure';
import { VideoFields, Video } from '../models/components/schemas/video/Video';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoFields = readonly VideoFields[];

/** Options for GET /videos/{video_id} */
export type GetVideoOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.video+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id} */
  query?: {
    /**
     * Whether to exclude the current user's ID from the player and download URLs.
     */
    excludeVideoLinksUserId?: boolean;

    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * Whether to return the responsive embed code.
     */
    responsive?: boolean;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;

    /**
     * Whether to return timestamps in the description as links.
     */
    timeLinks?: boolean;

    /**
     * The type of player and download URLs to return.
     *
     * Option descriptions:
     *  * `non_expiring` - Return URLs that don't expire.
     *
     */
    videoLinks?: `non_expiring`;
  };
};

/** Result type for GET /videos/{video_id} */
export type GetVideoResult<Paths extends readonly string[]> = Select<Video, Paths>;

/**
 * Get a specific video
 * This method returns a single video.
 */
export async function getVideo<Paths extends GetVideoFields>({
  baseUrl,
  select,
  where: { videoId },
  query,
  ...options
}: GetVideoOptions<Paths>): Promise<GetVideoResult<Paths>> {
  return measureLatency('getVideo', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetVideoResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type DeleteVideoFields = readonly string[];

/** Options for DELETE /videos/{video_id} */
export type DeleteVideoOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Data serialized to body for DELETE /videos/{video_id} */
  variables?: {
    /**
     * When true, the video is placed in the Recently Deleted purgatory bucket (30-day retention). When false or omitted, legacy user-delete purgatory applies.
     * @example true
     */

    sendToRecentlyDeleted?: boolean;
  };
};

/** Result type for DELETE /videos/{video_id} */
export type DeleteVideoResult<Paths extends readonly string[]> = unknown;

/**
 * Delete a video
 * This method deletes the specified video. The authenticated user must be the owner of the video.
 */
export async function deleteVideo<Paths extends DeleteVideoFields>({
  baseUrl,
  variables,
  where: { videoId },
  ...options
}: DeleteVideoOptions<Paths>): Promise<DeleteVideoResult<Paths>> {
  return measureLatency('deleteVideo', 'DELETE', async () => {
    let res = await fetch(`${baseUrl}/videos/${videoId}`, {
      ...options,
      method: 'DELETE',
      body: JSON.stringify(deepSnakeCase(variables)),
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as DeleteVideoResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PatchVideoFields = readonly VideoFields[];

/** Options for PATCH /videos/{video_id} */
export type PatchVideoOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.video+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Data serialized to body for PATCH /videos/{video_id} */
  variables: {
    /**
     * A list of values describing the content in this video. For a full list of values, use the [`/contentratings`](https://developer.vimeo.com/api/reference/videos#get_content_ratings) endpoint.
     */

    contentRating?: string[];

    /**
     * The ID of the Vimeo Create storyboard associated with the version of the video.
     * @example storyboard_123456_1000
     */

    createStoryboardId?: string;

    /**
     * The custom link of the video. This link doesn't include the base URL and the username or user ID of the video's owner.
     * @example puppies
     */

    customUrl?: string;

    /**
     * The description of the video. This field can hold a maximum of 5000 characters.
     * @example A celebration of 10 years of Staff Picks.
     */

    description?: string;

    embed?: {
      /**
       * Whether AirPlay is enabled in the embeddable player.
       * @example true
       */

      airplay?: boolean;

      /**
       * Whether Ask AI is enabled in the embeddable player.
       * @example true
       */

      askAi?: boolean;

      /**
       * Whether multiple audio tracks can appear in the embeddable player.
       * @example true
       */

      audioTracks?: boolean;

      buttons?: {
        /**
         * Whether to show the button for embeds on the embeddable player.
         * @example true
         */

        embed?: boolean;

        /**
         * Whether to show the button for fullscreen mode on the embeddable player.
         * @example true
         */

        fullscreen?: boolean;

        /**
         * Whether to show the button for HD video on the embeddable player.
         * @example true
         */

        hd?: boolean;

        /**
         * Whether to show the button for likes on the embeddable player.
         * @example true
         */

        like?: boolean;

        /**
         * Whether to show the button for reactions on the embeddable player.
         * @example true
         */

        reaction?: boolean;

        /**
         * Whether to show the button for scaling on the embeddable player in fullscreen mode.
         * @example true
         */

        scaling?: boolean;

        /**
         * Whether to show the button for shares on the embeddable player.
         * @example true
         */

        share?: boolean;

        /**
         * Whether to show the button for watching later on the embeddable player.
         * @example true
         */

        watchlater?: boolean;
      };

      /**
       * A collection of cards associated with the selected video.
       */

      cards?: {
        /**
         * The number of seconds for which the card appears.
         */

        displayTime?: number;

        /**
         * The title of the card.
         */

        headline?: string;

        /**
         * The UUID of the card.
         */

        id?: string;

        /**
         * The URL of the thumbnail for the card.
         */

        imageUrl?: string;

        /**
         * The description of the card.
         */

        teaser?: string;

        /**
         * The playback timestamp, given in seconds, when the card appears.
         */

        timecode?: number;

        /**
         * The URL of the card.
         */

        url?: string;
      }[];

      /**
       * Whether chapters are enabled in the embeddable player.
       * @example true
       */

      chapters?: boolean;

      /**
       * Whether the Chromecast button appears in the embeddable player.
       * @example true
       */

      chromecast?: boolean;

      /**
       * Whether closed captions are enabled in the embeddable player.
       * @example true
       */

      closedCaptions?: boolean;

      /**
       * The main color of the embeddable player.
       * @example #1ab7ea
       */

      color?: string;

      endScreen?: {
        /**
         * The end screen type.
         *
         * Option descriptions:
         *  * `empty` - The end screen is empty.
         *  * `loop` - The end screen loops the video playback.
         *  * `share` - The end screen includes sharing options.
         *  * `thumbnail` - The end screen includes the thumbnail of the video.
         *
         */

        type?: `empty` | `loop` | `share` | `thumbnail`;
      };

      logos?: {
        custom?: {
          /**
           * Whether to show the active custom logo on the embeddable player.
           * @example true
           */

          active?: boolean;

          /**
           * The ID of the custom logo that will show on the emeddable player.
           * @example 462119
           */

          id?: null | number;

          /**
           * The URL that loads when the user clicks the custom logo.
           * @example https://example.com
           */

          link?: null | string;

          /**
           * Whether the custom logo is always visible on the embeddable player (`true`) or whether the logo appears and disappears with the rest of the UI (`false`).
           * @example true
           */

          sticky?: boolean;
        };

        /**
         * Whether to show the Vimeo logo on the embeddable player.
         * @example true
         */

        vimeo?: boolean;
      };

      /**
       * An object representing the play button's settings.
       */

      playButton?: {
        /**
         * The position of the play button within the embeddable player.
         *
         * Option descriptions:
         *  * `auto` - Use Vimeo's default positioning for the play button.
         *  * `bottom` - The play button is positioned at the bottom of the player, except when in tiny mode.
         *  * `center` - The play button is positioned in the center of the player.
         *
         */

        position?: `auto` | `bottom` | `center`;
      };

      /**
       * Whether to show the playbar on the embeddable player.
       * @example true
       */

      playbar?: boolean;

      /**
       * Whether to show the quality selector in the embeddable player.
       * @example true
       */

      qualitySelector?: boolean;

      /**
       * A collection of sentiment widget configurations for the video.
       */

      sentimentWidgets?: {
        /**
         * The playback position in seconds when the widget disappears.
         */

        endTime?: number;

        /**
         * The headline text shown above the widget.
         */

        headline?: string;

        /**
         * The unique identifier of the sentiment widget.
         */

        id?: string;

        /**
         * The playback position in seconds when the widget appears.
         */

        startTime?: number;

        /**
         * The widget variant.
         *
         * Option descriptions:
         *  * `ranking` - The widget shows a ranking of options.
         *  * `thumbs` - The widget shows thumbs up/down buttons.
         *
         */

        type?: `ranking` | `thumbs`;
      }[];

      /**
       * Whether users can skip forward in the embeddable player.
       * @example true
       */

      skippingForward?: boolean;

      /**
       * Whether to show speed options in the embeddable player.
       * @example true
       */

      speed?: boolean;

      title?: {
        /**
         * How to handle the video title in the title bar of the embeddable player.
         *
         * Option descriptions:
         *  * `hide` - Hide the video title.
         *  * `show` - Show the video title.
         *  * `user` - Enable the user to decide.
         *
         */

        name?: `hide` | `show` | `user`;

        /**
         * How to handle the owner information in the title bar of the embeddable player.
         *
         * Option descriptions:
         *  * `hide` - Hide the owner info.
         *  * `show` - Show the owner info.
         *  * `user` - Enable the user to decide.
         *
         */

        owner?: `hide` | `show` | `user`;

        /**
         * How to handle the owner portrait in the title bar of the embeddable player.
         *
         * Option descriptions:
         *  * `hide` - Hide the portrait.
         *  * `show` - Show the portrait.
         *  * `user` - Enable the user to decide.
         *
         */

        portrait?: `hide` | `show` | `user`;
      };

      /**
       * Whether the transcript controls appear in the embeddable player.
       * @example true
       */

      transcript?: boolean;

      /**
       * Whether to show the volume selector on the embeddable player.
       * @example true
       */

      volume?: boolean;
    };

    /**
     * The complete list of domains the video can be embedded on. This field overwrites existing domains and requires that **privacy_embed** have the value `whitelist`.
     */

    embedDomains?: string[];

    /**
     * A list of domains intended to be added to an existing set of domains. This field requires that **privacy_embed** have the value `whitelist`.
     */

    embedDomainsAdd?: string[];

    /**
     * A list of domains intended to be removed from an existing set of domains. This field requires that **privacy_embed** have the value `whitelist`.
     */

    embedDomainsDelete?: string[];

    /**
     * Whether the video's storage quota should be exempted.
     */

    exemptQuota?: boolean;

    /**
     * The ID of the folder associated with the video. Only the video owner or a team member can associate a video with a folder.
     * @example 1234
     */

    folderId?: number;

    /**
     * Whether the video has interactive capability.
     */

    hasInteractive?: boolean;

    /**
     * Whether to hide the video from everyone except the video's owner. When the value is `true`, unlisted video links work only for the video's owner.
     */

    hideFromVimeo?: boolean;

    /**
     * Whether the video is being edited by the Transcript Video Editing flow.
     */

    isEditedByTve?: boolean;

    /**
     * The Creative Commons license under which the video is offered.
     *
     * Option descriptions:
     *  * `by` - The video is offered under CC BY, or the attibution-only license.
     *  * `by-nc` - The video is offered under CC BY-NC, or the Attribution-NonCommercial license.
     *  * `by-nc-nd` - The video is offered under CC BY-NC-ND, or the Attribution-NonCommercian-NoDerivs license.
     *  * `by-nc-sa` - The video is offered under CC BY-NC-SA, or the Attribution-NonCommercial-ShareAlike licence.
     *  * `by-nd` - The video is offered under CC BY-ND, or the Attribution-NoDerivs license.
     *  * `by-sa` - The video is offered under CC BY-SA, or the Attribution-ShareAlike license.
     *  * `cc0` - The video is offered under CC0, or public domain, videos.
     *
     */

    license?: `by` | `by-nc` | `by-nc-nd` | `by-nc-sa` | `by-nd` | `by-sa` | `cc0`;

    live?: {
      /**
       * An array of destinations to which to simulcast the live stream.
       */

      simulcastDestinations?: {
        metadata?: {
          /**
           * The description of the simulcast. This field applies to Facebook and YouTube destinations.
           * @example Our Q2 company all hands.
           */

          description?: string;

          /**
           * The ID of the event associated with the simulcast. This field applies to Livestream destinations.
           * @example 12345
           */

          eventId?: string;

          /**
           * The ID of the object that contains the video on the host service. This field applies to Facebook destinations.
           * @example 12345
           */

          parentId?: string;

          /**
           * The privacy level of the stream on the simulcast destination. This field applies to Facebook and YouTube destinations.
           *
           * Option descriptions:
           *  * `all_friends` - Facebook: The owner and their friends can access the stream.
           *  * `custom` - Facebook: A user-specified allowlist of permitted Facebook members can access the stream.
           *  * `everyone` - Facebook: Anyone can access the stream.
           *  * `friends_of_friends` - Facebook: The owner, their friends, and the friends of their friends can access the stream.
           *  * `private` - YouTube: The owner can access the stream.
           *  * `public` - YouTube: Anyone can access the stream.
           *  * `self` - Facebook: The owner of the Facebook profile can access the stream.
           *  * `unlisted` - YouTube: Anyone can access the stream, but it isn't searchable.
           *
           */

          privacy?:
            | `all_friends`
            | `custom`
            | `everyone`
            | `friends_of_friends`
            | `private`
            | `public`
            | `self`
            | `unlisted`;

          /**
           * Whether the simulcast is automatically tweeted. This field applies to Periscope destinations.
           */

          shouldTweet?: boolean;

          /**
           * The title of the simulcast. This field applies to YouTube, Facebook, Periscope, and Livestream destinations.
           * @example Quarterly Meeting
           */

          title?: string;

          /**
           * The type of the object that contains the simulcast video on the host service. This field applies to Facebook destinations.
           *
           * Option descriptions:
           *  * `event` - The simulcast is in a Facebook event.
           *  * `group` - The simulcast is in a Facebook group.
           *  * `page` - The simulcast is on a Facebook page.
           *  * `profile` - The simulcast is in a Facebook profile.
           *  * `scheduled_broadcast` - The simulcast is in a Facebook Live scheduled broadcast.
           *
           */

          type?: `event` | `group` | `page` | `profile` | `scheduled_broadcast`;

          /**
           * The host-service ID of the user who owns the simulcast video. This field applies to Facebook, YouTube, Periscope, and Livestream destinations.
           * @example 12345
           */

          userId?: string;

          /**
           * The host-service ID of the simulcast video. This field applies to Facebook, YouTube, and Periscope destinations.
           * @example 12345
           */

          videoId?: string;
        };

        /**
         * The RTMP URL of a destination beyond Vimeo where the simulcast should be pushed.
         * @example rtmp://server?token=rr32245t-4534454t-ggrg
         */

        rtmpLink?: string;

        /**
         * The name of the external host of the simulcast.
         *
         * Option descriptions:
         *  * `custom_rtmp` - The host is customer-specified.
         *  * `facebook` - The host is Facebook Live.
         *  * `livestream` - The host is Livestream.
         *  * `periscope` - The host is Periscope.
         *  * `youtube` - The host is YouTube Live.
         *
         */

        serviceName?: `custom_rtmp` | `facebook` | `livestream` | `periscope` | `youtube`;

        /**
         * The stream key used to authenticate the simulcast.
         * @example rr32245t-4534454t-ggrg
         */

        streamKey?: string;
      }[];

      /**
       * Whether to hide the video from its owner during streaming.
       * @example true
       */

      simulcastOnly?: boolean;

      /**
       * The status of the live stream.
       *
       * Option descriptions:
       *  * `done` - Finish a live stream.
       *  * `ready` - Start a live-stream-initialized video into streaming mode.
       *
       */

      status?: `done` | `ready`;
    };

    /**
     * The video's default language. For a full list of supported languages, use the [`/languages?filter=texttracks`](https://developer.vimeo.com/api/reference/videos#get_languages) endpoint.
     * @example en-US
     */

    locale?: string;

    metadata?: {
      /**
       * Information about the video's AI content.
       */

      aiContent?: {
        /**
         * Whether the video contains AI-generated audio.
         * @example true
         */

        audio?: boolean;

        /**
         * Whether the video contains AI-generated audio that is marked automatically.
         * @example true
         */

        audioAuto?: boolean;

        /**
         * Whether the video contains AI-generated visuals.
         * @example true
         */

        visual?: boolean;

        /**
         * Whether the video contains AI-generated visuals that are marked automatically.
         * @example true
         */

        visualAuto?: boolean;
      };

      /**
       * Whether the video has chapter suggestions.
       */

      hasChapterSuggestions?: boolean;
    };

    /**
     * The title of the video. This field can hold a maximum of 128 characters.
     * @example Celebrating 10 Years of Staff Picks
     */

    name?: string;

    /**
     * Whether the video has unified resolution. If the value of this field is `false`, the video requires transcoding.
     */

    originVariableFrameResolution?: boolean;

    /**
     * Information about the display of the video page.
     */

    page?: {
      /**
       * Whether to display the button for Vimeo AI on the video page.
       * @example true
       */

      askAi?: boolean;

      /**
       * Whether to display video categories on the video page.
       * @example true
       */

      categories?: boolean;

      /**
       * Whether to display the button for chapters on the video page.
       * @example true
       */

      chapters?: boolean;

      /**
       * Whether to display collections on the video page.
       * @example true
       */

      collections?: boolean;

      /**
       * Whether to display comments on the video page.
       * @example true
       */

      comments?: boolean;

      /**
       * Whether to display the video's Creative Commons license on the video page.
       * @example true
       */

      creativeCommons?: boolean;

      /**
       * Whether to display credits for the video on the video page.
       * @example true
       */

      credits?: boolean;

      /**
       * The contents of the page customization group (background color, title font, and inline logo).
       */

      customization?: {
        /**
         * The page background color as a `#`-prefixed hex string, or null to clear.
         * @example #ff0000
         */

        backgroundColor?: null | string;

        /**
         * Optional logo to display inline with the video title.
         */

        logo?: {
          /**
           * The canonical CDN URL of the selected custom logo. The server resolves the path to the underlying brand-kit or player logo, enforces ownership, and persists only the re-derived URL — any non-canonical input is rejected.
           * @example https://i.vimeocdn.com/customlogo/abc_640
           */

          customLogoUrl?: null | string;

          /**
           * Whether to display the logo next to the title.
           */

          enabled?: boolean;
        };

        /**
         * The selected video title font family.
         */

        titleFontFamily?:
          | null
          | `anton`
          | `archivo_black`
          | `bebas_neue`
          | `bricolage_grotesque`
          | `caveat`
          | `dm_sans`
          | `dm_serif_display`
          | `fraunces`
          | `hanken_grotesk`
          | `ibm_plex_serif`
          | `instrument_serif`
          | `inter`
          | `jetbrains_mono`
          | `lora`
          | `montserrat`
          | `outfit`
          | `playfair_display`
          | `poppins`
          | `roboto`
          | `space_grotesk`;

        /**
         * The selected video title font weight.
         *
         * Option descriptions:
         *  * `300` - Light
         *  * `400` - Regular
         *  * `500` - Medium
         *  * `700` - Bold
         *
         */

        titleFontWeight?: null | 300 | 400 | 500 | 700;
      };

      /**
       * Whether to display the date when the video was added on the video page.
       * @example true
       */

      dateAdded?: boolean;

      /**
       * Whether to display a description of the video on the video page.
       * @example true
       */

      description?: boolean;

      /**
       * Whether to display the button for downloads on the video page.
       * @example true
       */

      download?: boolean;

      /**
       * Whether to display the button for likes on the video page.
       * @example true
       */

      like?: boolean;

      /**
       * Whether to display the video owner's user name on the video page.
       */

      owner?: boolean;

      /**
       * The contents of the on-site player configuration.
       */

      player?: {
        /**
         * Whether AirPlay is enabled in the video page player.
         * @example true
         */

        airplay?: boolean;

        /**
         * Whether multiple audio tracks can appear in the video page player.
         * @example true
         */

        audioTracks?: boolean;

        /**
         * Whether the Chromecast button appears in the video page player.
         * @example true
         */

        chromecast?: boolean;

        /**
         * Whether closed captions are enabled in the video page player.
         * @example true
         */

        closedCaptions?: boolean;

        /**
         * A collection of information about the colors in the video page player.
         */

        colors?: {
          /**
           * The fourth player color, which controls the player background color.
           * @example #00adef
           */

          colorFour?: string;

          /**
           * The first player color, which controls the color of the progress bar, buttons, and more.
           * @example #00adef
           */

          colorOne?: string;

          /**
           * The third player color, which controls the color of text and icons.
           * @example #000000
           */

          colorThree?: string;

          /**
           * The second player color, which controls the player accent color.
           * @example #00adef
           */

          colorTwo?: string;
        };

        /**
         * Whether to display the button for fullscreen mode on the video page.
         * @example true
         */

        fullscreen?: boolean;

        /**
         * A collection of information about the logo in the corner of the video page player.
         */

        logos?: {
          /**
           * A collection of information relating to custom logos in the video page player.
           */

          custom?: {
            /**
             * Whether the custom logo appears in the video page player.
             * @example true
             */

            active?: boolean;

            /**
             * The URL that loads upon clicking the custom logo.
             * @example http://example.com
             */

            link?: null | string;

            /**
             * Whether the custom logo appears even when the player interface is hidden.
             * @example true
             */

            sticky?: boolean;

            /**
             * The URL of the selected custom logo.
             * @example http://example.com
             */

            url?: null | string;

            /**
             * Whether the custom logo should use the URL link.
             * @example true
             */

            useLink?: boolean;
          };

          /**
           * Whether the Vimeo logo appears in the video page player.
           * @example true
           */

          vimeo?: boolean;
        };

        /**
         * Whether picture-in-picture is enabled in the video page player.
         * @example true
         */

        pip?: boolean;

        /**
         * A representation of the play button's settings.
         */

        playButton?: {
          /**
           * The position of the play button within the video page player.
           *
           * Option descriptions:
           *  * `auto` - Use Vimeo's default positioning for the play button.
           *  * `bottom` - The play button is positioned at the bottom of the player, except when in tiny mode.
           *  * `center` - The play button is positioned in the center of the player.
           *
           */

          position?: `auto` | `bottom` | `center`;
        };

        /**
         * Whether to display the playbar on the video page.
         * @example true
         */

        playbar?: boolean;

        /**
         * Whether to display the quality selector in the video page player.
         * @example true
         */

        qualitySelector?: boolean;

        /**
         * Whether to display speed options in the video page player.
         * @example true
         */

        speed?: boolean;

        /**
         * Whether to display the Vimeo logo on the video page.
         * @example true
         */

        vimeoLogo?: boolean;

        /**
         * Whether to display the volume control on the video page.
         * @example true
         */

        volume?: boolean;
      };

      /**
       * Whether to display the video owner's avatar on the video page.
       * @example true
       */

      portrait?: boolean;

      /**
       * Whether to display the button for shares on the video page.
       * @example true
       */

      share?: boolean;

      /**
       * Whether to display tags for the video on the video page.
       * @example true
       */

      tags?: boolean;

      /**
       * Whether to display the video title on the video page.
       * @example true
       */

      title?: boolean;

      /**
       * Whether to display the transcript on the video page.
       * @example true
       */

      transcript?: boolean;

      /**
       * Whether to display the video's uploader on the video page.
       * @example true
       */

      uploader?: boolean;

      /**
       * Whether to display the number of views that the video has received on the video page.
       * @example true
       */

      views?: boolean;

      /**
       * Whether to display the button for watching later on the video page.
       * @example true
       */

      watchLater?: boolean;
    };

    /**
     * The password. When you set **privacy.view** to `password`, you must provide the password as an additional parameter. This field can hold a maximum of 32 characters.
     * @example hunter1
     */

    password?: string;

    privacy?: {
      /**
       * Whether a user can add the video to a showcase, channel, or group.
       * @example true
       */

      add?: boolean;

      /**
       * Whether the share link is enabled.
       * @example true
       */

      allowShareLink?: boolean;

      /**
       * The privacy level required to comment on the video.
       *
       * Option descriptions:
       *  * `anybody` - Anyone can comment on the video.
       *  * `contacts` - Only the owner's contacts can comment on the video.
       *  * `nobody` - No one can comment on the video.
       *
       */

      comments?: `anybody` | `contacts` | `nobody`;

      /**
       * Whether a user can download the video. This field isn't available to Vimeo Free members.
       * @example true
       */

      download?: boolean;

      /**
       * The video's embed setting. Specify the `whitelist` value to restrict embedding to a specific set of domains. For more information, see our [Interacting with Videos](https://developer.vimeo.com/api/guides/videos/interact#set-off-site-privacy) guide.
       *
       * Option descriptions:
       *  * `private` - The video can't be embedded.
       *  * `public` - The video can be embedded.
       *  * `whitelist` - The video can be embedded on the specified domains only.
       *
       */

      embed?: `private` | `public` | `whitelist`;

      /**
       * The video's privacy setting. When this value is `users`, `application/json` is the only valid content type. Also, some privacy settings are unavailable to Vimeo Free members; for more information, see our [Help Center](https://vimeo.zendesk.com/hc/en-us/articles/224817847).
       *
       * Option descriptions:
       *  * `anybody` - Anyone can access the video. This privacy setting appears as `Public` on the Vimeo front end.
       *  * `contacts` - Only those who follow the owner on Vimeo can access the video. _This field is deprecated._
       *  * `disable` - The video is embeddable, but it's hidden on Vimeo and can't be played. This privacy setting appears as `Hide from Vimeo` on the Vimeo front end.
       *  * `nobody` - No one except the owner can access the video. This privacy setting appears as `Private` on the Vimeo front end.
       *  * `password` - Only those with the password can access the video.
       *  * `team` - Anyone on the team can access the video. This privacy setting appears as `Anyone at Company` on the Vimeo front end.
       *  * `unlisted` - Only those with the private link can access the video.
       *  * `users` - Only Vimeo members can access the video. _This field is deprecated._
       *
       */

      view?:
        | `anybody`
        | `contacts`
        | `disable`
        | `nobody`
        | `password`
        | `team`
        | `unlisted`
        | `users`;
    };

    privateComments?: {
      /**
       * Whether to enable private comments.
       * @example true
       */

      isEnabled?: boolean;
    };

    /**
     * The hash that represents the connection between the video and other assets, including Create videos. Use this hash to find assets related to the video.
     * @example O1QcJ0dUAC88BgNgCzE
     */

    resultVideoHash?: string;

    reviewPage?: {
      /**
       * Whether to enable video review.
       * @example true
       */

      active?: boolean;

      /**
       * Whether to make the review page accessible to users who aren't on the owner's team.
       * @example true
       */

      isShareable?: boolean;

      /**
       * Whether to enable review comments.
       * @example true
       */

      notes?: boolean;

      /**
       * Whether to show the Vimeo logo on the review page.
       * @example true
       */

      vimeoLogo?: boolean;
    };

    /**
     * The review status of the video.
     *
     * Option descriptions:
     *  * `approved` - The video review was approved.
     *  * `in_progress` - The video review is in progress.
     *  * `needs_review` - The video needs to be reviewed.
     *
     */

    reviewStatus?: `approved` | `in_progress` | `needs_review`;

    /**
     * If **approach** is `live`, the time in ISO 8601 format when the user intends to start streaming.
     * @example 2017-05-16T15:05:43+00:00
     */

    scheduledStartTime?: string;

    spatial?: {
      /**
       * An array representing the 360 director timeline.
       */

      directorTimeline?: {
        /**
         * The 360 director timeline pitch. This value must be between −90 and 90, and you must specify it only when **spatial.director_timeline** is defined.
         * @example 70
         */

        pitch: number;

        /**
         * The 360 director timeline roll.
         */

        roll?: number;

        /**
         * The 360 director timeline time code. This paramater is required only when **spatial.director_timeline** is defined.
         * @example 5
         */

        timeCode: number;

        /**
         * The 360 director timeline yaw. This value must be between 0 and 360, and you must specify it only when **spatial.director_timeline** is defined.
         * @example 125
         */

        yaw: number;
      }[];

      /**
       * The 360 field of view. This value must be between 30 and 90. The default is 50.
       * @example 50
       */

      fieldOfView?: number;

      /**
       * The 360 spatial projection.
       *
       * Option descriptions:
       *  * `cubical` - Use cubical projection.
       *  * `cylindrical` - Use cylindrical projection.
       *  * `dome` - Use dome projection.
       *  * `equirectangular` - Use equirectangular projection.
       *  * `pyramid` - Use pyramid projection.
       *
       */

      projection?: `cubical` | `cylindrical` | `dome` | `equirectangular` | `pyramid`;

      /**
       * The 360 spatial stereo format.
       *
       * Option descriptions:
       *  * `left-right` - Use left-right stereo.
       *  * `mono` - Use monaural audio.
       *  * `top-bottom` - Use top-bottom stereo.
       *
       */

      stereoFormat?: `left-right` | `mono` | `top-bottom`;
    };

    user?: {
      /**
       * The video owner's account type.
       *
       * Option descriptions:
       *  * `advanced` - The user has a Vimeo Advanced subscription.
       *  * `basic` - The user has a Vimeo Basic subscription.
       *  * `business` - The user has a Vimeo Business subscription.
       *  * `enterprise` - The user has a Vimeo Enterprise subscription.
       *  * `free` - The user has a Vimeo Free subscription.
       *  * `live_business` - The user has a Vimeo Business Live subscription.
       *  * `live_premium` - The user has a Vimeo Premium subscription.
       *  * `live_pro` - The user has a Vimeo PRO Live subscription.
       *  * `plus` - The user has a Vimeo Plus subscription.
       *  * `pro` - The user has a Vimeo Pro subscription.
       *  * `pro_unlimited` - The user has a Vimeo PRO Unlimited subscription.
       *  * `producer` - The user has a Vimeo Producer subscription.
       *  * `standard` - The user has a Vimeo Standard subscription.
       *  * `starter` - The user has a Vimeo Starter subscription.
       *
       * @example basic
       */

      account?:
        | `advanced`
        | `basic`
        | `business`
        | `enterprise`
        | `free`
        | `live_business`
        | `live_premium`
        | `live_pro`
        | `plus`
        | `pro`
        | `pro_unlimited`
        | `producer`
        | `standard`
        | `starter`;
    };

    /**
     * Whether to enable DRM for a video.
     * @example true
     */

    usesDrm?: boolean;
  };
};

/** Result type for PATCH /videos/{video_id} */
export type PatchVideoResult<Paths extends readonly string[]> = Select<Video, Paths>;

/**
 * Edit a video
 * This method edits the specified video.
 */
export async function patchVideo<Paths extends PatchVideoFields>({
  baseUrl,
  select,
  variables,
  where: { videoId },
  ...options
}: PatchVideoOptions<Paths>): Promise<PatchVideoResult<Paths>> {
  return measureLatency('patchVideo', 'PATCH', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}?fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'PATCH',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PatchVideoResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
